'use client'

import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { type CommonDialogProps, type DialogConfig } from '@/features/dialogs/common/types'
import { useELendDetails, type ELendDetailsData } from '@/features/elend-details/logic/useELendDetails'
import { EEarnDepositDialogContent } from '@/features/evault-details/dialogs/deposit/EEarnDepositDialogContent'
import { EvcDepositDialogContent } from '@/features/evault-details/dialogs/deposit/EvcDepositDialogContent'
import { useEVaultDetails, type EVaultDetailsData } from '@/features/evault-details/logic/useEVaultDetails'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { useMemo } from 'react'
import type { Address } from 'viem'

export interface MarketDepositActionDialogProps extends CommonDialogProps {
  vaultAddress: Address
  chainId: number
  source: 'evault' | 'elend'
}

function LoadingState() {
  return (
    <div className="flex min-h-[420px] flex-col gap-4 p-6">
      <div className="h-7 w-40 animate-pulse rounded-md bg-white/[0.08]" />
      <div className="h-12 w-full animate-pulse rounded-lg bg-white/[0.08]" />
      <div className="h-28 w-full animate-pulse rounded-lg bg-white/[0.08]" />
      <div className="h-12 w-full animate-pulse rounded-lg bg-white/[0.08]" />
    </div>
  )
}

const ZERO_ADDRESS: Address = '0x0000000000000000000000000000000000000000'

function elendToEVaultDetails(elend: ELendDetailsData): EVaultDetailsData {
  return {
    address: elend.address,
    name: elend.name,
    symbol: elend.symbol,
    decimals: elend.decimals,
    totalSupply: elend.totalSupply,
    underlyingAsset: elend.underlyingAsset,
    assetSymbol: elend.assetSymbol,
    underlyingTokenInfo: elend.underlyingTokenInfo,
    supplyAPY: Number(elend.supplyAPY),
    sharePrice: elend.sharePrice || NormalizedUnitNumber('1'),
    totalAssets: elend.totalAssets,
    lastTotalAssets: elend.totalAssets,
    exchangeRate: 1,
    owner: elend.governor || ZERO_ADDRESS,
    guardian: elend.governor || ZERO_ADDRESS,
    curator: elend.governor || ZERO_ADDRESS,
    timelock: 0n,
    performanceFee: 0n,
    oracle: elend.oracle,
    unitOfAccount: elend.unitOfAccount,
    userBalance: elend.userBalance,
    supplyCap: elend.supplyCap,
  }
}

function useUnderlyingDepositToken(evault: EVaultDetailsData | undefined): Token | undefined {
  const { priceUSD } = useAssetPrice({
    assetAddress: evault?.underlyingAsset,
    vaultAddress: evault?.address,
    lendingVaultAddresses: evault?.lendingVaultAddresses,
    chainId: E_EARN_VAULT_CHAIN_ID,
    enabled: !!evault?.underlyingAsset,
  })

  return useMemo(() => {
    if (!evault) return undefined
    const info = evault.underlyingTokenInfo
    const symbol = info?.symbol || evault.assetSymbol
    const decimals = info?.decimals
    const name = info?.name || symbol
    if (!symbol || decimals === undefined) return undefined
    return new Token({
      symbol: TokenSymbol(symbol),
      name: name || symbol,
      decimals,
      address: CheckedAddress(evault.underlyingAsset),
      unitPriceUsd: priceUSD.toString(),
      logoURI: info?.logoURI,
    })
  }, [evault, priceUSD])
}

function ELendDepositPanel({ vaultAddress, chainId, setOpen }: { vaultAddress: Address; chainId: number; setOpen: (open: boolean) => void }) {
  const { elend, isLoading } = useELendDetails({ vaultAddress, chainId })
  const evault = useMemo(() => (elend ? elendToEVaultDetails(elend) : undefined), [elend])
  const depositToken = useUnderlyingDepositToken(evault)

  if (isLoading || !evault || !depositToken) {
    return <LoadingState />
  }

  return (
    <EvcDepositDialogContent
      closeDialog={() => setOpen(false)}
      evault={evault}
      depositAmount=""
      depositToken={depositToken}
      isNativeDeposit={false}
    />
  )
}

function EVaultDepositPanel({ vaultAddress, setOpen }: { vaultAddress: Address; setOpen: (open: boolean) => void }) {
  const { evault, isLoading } = useEVaultDetails({ vaultAddress: CheckedAddress(vaultAddress) })
  const depositToken = useUnderlyingDepositToken(evault ?? undefined)

  if (isLoading || !evault || !depositToken) {
    return <LoadingState />
  }

  return (
    <EEarnDepositDialogContent
      closeDialog={() => setOpen(false)}
      evault={evault}
      depositAmount=""
      depositToken={depositToken}
      isNativeDeposit={false}
      vaultType="eEarn"
    />
  )
}

function MarketDepositActionDialog({ open, setOpen, vaultAddress, chainId, source }: MarketDepositActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle className="sr-only">Deposit</DialogTitle>
        {source === 'elend' ? (
          <ELendDepositPanel vaultAddress={vaultAddress} chainId={chainId} setOpen={setOpen} />
        ) : (
          <EVaultDepositPanel vaultAddress={vaultAddress} setOpen={setOpen} />
        )}
      </DialogContent>
    </Dialog>
  )
}

export const marketDepositActionDialogConfig: DialogConfig<MarketDepositActionDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: MarketDepositActionDialog,
}
